import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  Stack,
} from '@mui/material';
import {
  Create as CreateIcon,
  LocalShipping as ShippingIcon,
  AutoAwesome as MagicIcon,
  Palette as ArtistIcon,
} from '@mui/icons-material';
import { ImageComparison } from '../components/ImageComparison';

const features = [
  {
    icon: <MagicIcon sx={{ fontSize: 40 }} />,
    title: 'AI-Powered Creation',
    description: 'Transform your ideas into stunning postcards with our advanced AI technology.',
  },
  {
    icon: <ArtistIcon sx={{ fontSize: 40 }} />,
    title: 'Respect Artists',
    description: 'We respect artists and their work. We will pay them a commission for each postcard sold.',
  },
  {
    icon: <CreateIcon sx={{ fontSize: 40 }} />,
    title: 'Personalized Messages',
    description: 'Add your heartfelt message to make each postcard truly special.',
  },
  {
    icon: <ShippingIcon sx={{ fontSize: 40 }} />,
    title: 'Worldwide Delivery',
    description: 'We handle printing and shipping to any address worldwide.',
  },
];

const Home: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                Send Joy Through AI-Crafted Postcards
              </Typography>
              <Typography
                variant="h5"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Create personalized postcards with AI, add your message, and we'll deliver them worldwide.
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Honour artists and their work by choosing us. We pay them a commission for each postcard sold, helping them to keep creating in this digital age.
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/gallery')}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  Create Postcard
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/gallery')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  View Gallery
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <ImageComparison
                before="/hero/retro-before.png"
                reference="/hero/retro-reference.jpg"
                after="/hero/retro-after.png"
                width={150}
                height={250}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          variant="h2"
          align="center"
          sx={{ mb: 6 }}
        >
          Why Choose Us?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    color: 'primary.main',
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h5" gutterBottom>
                  {feature.title}
                </Typography>
                <Typography color="text.secondary">
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'grey.50',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="md">
          <Card
            sx={{
              p: { xs: 3, md: 6 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)',
              color: 'white',
            }}
          >
            <Typography variant="h3" gutterBottom>
              Ready to Create Your First Postcard?
            </Typography>
            <Typography
              variant="h6"
              sx={{ mb: 4, opacity: 0.9 }}
            >
              Be one of our first happy customers sending joy through our platform.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/gallery')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              Get Started Now
            </Button>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 